<div *ngIf="isOpen" class="modal-overlay" (click)="close()">
  <ng-container *ngIf="showCreatedKey && createdKey; else apiKeyManager">
    <div class="created-key-modal" (click)="$event.stopPropagation()">
      <div class="created-key-content">
        <div class="success-icon">
          <span class="material-icons">check_circle</span>
        </div>
        <h3>API Key Created Successfully!</h3>
        <p class="warning-text">⚠️ Save this key now - you won't be able to see it again!</p>
        
        <div class="key-display">
          <div class="key-label">API Key:</div>
          <div class="key-value">
            <code>{{ createdKey.apiKey }}</code>
            <button class="copy-button" (click)="copyToClipboard(createdKey.apiKey)">
              <span class="material-icons">content_copy</span>
            </button>
          </div>
          <div class="copy-feedback" *ngIf="copied">Copied!</div>
        </div>

        <div class="key-info">
          <div class="info-row">
            <span class="label">Name:</span>
            <span class="value">{{ createdKey.name || 'Unnamed Key' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Prefix:</span>
            <span class="value">{{ createdKey.keyPrefix }}</span>
          </div>
        </div>

        <button class="btn-primary" (click)="closeCreatedKeyModal()">I've saved my key</button>
      </div>
    </div>
  </ng-container>

  <ng-template #apiKeyManager>
    <div class="modal-container" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>API Key Management</h2>
        <button class="close-button" (click)="close()">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div *ngIf="error" class="error-banner">
        <span class="material-icons">error</span>
        <span>{{ error }}</span>
      </div>

      <div *ngIf="isLoading" class="loading-overlay">
        <div class="spinner"></div>
      </div>

      <div class="modal-body">
      <div class="actions-bar">
        <button 
          class="btn-primary" 
          (click)="openCreateForm()" 
          *ngIf="!showCreateForm"
          [disabled]="isLoading">
          <span class="material-icons">add</span>
          Create New API Key
        </button>
      </div>

      <div *ngIf="showCreateForm" class="create-form">
        <h3>Create New API Key</h3>
        <div class="form-group">
          <label for="keyName">Name (optional)</label>
          <input 
            id="keyName"
            type="text" 
            [(ngModel)]="newKeyName" 
            placeholder="e.g., Production API Key"
            class="form-control">
        </div>
        <div class="form-group">
          <label for="keyDescription">Description (optional)</label>
          <textarea 
            id="keyDescription"
            [(ngModel)]="newKeyDescription" 
            placeholder="Describe the purpose of this key..."
            class="form-control"
            rows="3"></textarea>
        </div>
        <div class="form-actions">
          <button class="btn-secondary" (click)="cancelCreate()" [disabled]="isLoading">Cancel</button>
          <button class="btn-primary" (click)="createApiKey()" [disabled]="isLoading">Create</button>
        </div>
      </div>

      <div class="api-keys-list" *ngIf="!showCreateForm">
        <h3>Your API Keys</h3>
        
        <div *ngIf="apiKeys.length === 0 && !isLoading" class="empty-state">
          <span class="material-icons">vpn_key</span>
          <p>No API keys yet</p>
          <p class="subtitle">Create your first API key to get started</p>
        </div>

        <div class="keys-container">
          <div *ngFor="let key of apiKeys" class="key-card" [class.disabled]="!key.enabled">
            <div *ngIf="editingKeyId !== key.id" class="key-view">
              <div class="key-header">
                <div class="key-title-section">
                  <h4>{{ key.name || 'Unnamed Key' }}</h4>
                  <span class="key-prefix">{{ key.keyPrefix }}...</span>
                  <span class="status-badge" [class.active]="key.enabled" [class.inactive]="!key.enabled">
                    {{ key.enabled ? 'Active' : 'Disabled' }}
                  </span>
                </div>
                <div class="key-actions">
                  <button 
                    class="icon-button toggle-button" 
                    (click)="toggleKeyStatus(key)"
                    [title]="key.enabled ? 'Disable key' : 'Enable key'">
                    <span class="material-icons">{{ key.enabled ? 'toggle_on' : 'toggle_off' }}</span>
                  </button>
                  <button class="icon-button" (click)="startEdit(key)" title="Edit key">
                    <span class="material-icons">edit</span>
                  </button>
                  <button class="icon-button danger" (click)="deleteApiKey(key.id)" title="Delete key">
                    <span class="material-icons">delete</span>
                  </button>
                </div>
              </div>
              
              <div class="key-details">
                <div class="detail-row" *ngIf="key.description">
                  <span class="material-icons">description</span>
                  <span>{{ key.description }}</span>
                </div>
                <div class="detail-row">
                  <span class="material-icons">event</span>
                  <span>Created: {{ formatDate(key.createdAt) }}</span>
                </div>
                <div class="detail-row" *ngIf="key.lastUsedAt">
                  <span class="material-icons">access_time</span>
                  <span>Last used: {{ formatDate(key.lastUsedAt) }}</span>
                </div>
                <div class="detail-row" *ngIf="!key.lastUsedAt">
                  <span class="material-icons">access_time</span>
                  <span>Never used</span>
                </div>
              </div>
            </div>

            <div *ngIf="editingKeyId === key.id" class="key-edit">
              <div class="form-group">
                <label>Name</label>
                <input 
                  type="text" 
                  [(ngModel)]="editForm.name" 
                  class="form-control"
                  placeholder="Key name">
              </div>
              <div class="form-group">
                <label>Description</label>
                <textarea 
                  [(ngModel)]="editForm.description" 
                  class="form-control"
                  rows="2"
                  placeholder="Key description"></textarea>
              </div>
              <div class="form-group checkbox-group">
                <label>
                  <input 
                    type="checkbox" 
                    [(ngModel)]="editForm.enabled">
                  <span>Enabled</span>
                </label>
              </div>
              <div class="form-actions">
                <button class="btn-secondary" (click)="cancelEdit()" [disabled]="isLoading">Cancel</button>
                <button class="btn-primary" (click)="saveEdit(key.id)" [disabled]="isLoading">Save</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </ng-template>
</div>

